/**
 * Hook pro správu Web Push subscription.
 *
 * Použití:
 *   const { isSupported, permission, subscribe, unsubscribe, isSubscribed } = usePushSubscription()
 *
 * VAPID public key se načte z /api/push/vapid-public-key.
 * Subscription se odešle na /api/push/subscribe (vyžaduje JWT token).
 *
 * **Auto-heal:** při mountu — pokud je povolení už uděleno (`granted`) a je k
 * dispozici token — hook idempotentně zajistí, že browser subscription existuje
 * a je zaregistrovaná na serveru. Řeší dva případy:
 *   1. Povolení uděleno mimo tento hook (např. při instalaci PWA) → subscription
 *      nikdy nevznikla → UI by jinak ukazovalo „vypnuto" i přes udělené povolení.
 *   2. Subscription v prohlížeči existuje, ale server ji po resetu/migraci
 *      zapomněl → znovu ji zaregistrujeme (upsert je idempotentní).
 * Auto-heal NIKDY nevyvolá systémový prompt (jen když je `permission==="granted"`).
 */
import { useState, useEffect, useCallback } from "react";

interface UsePushSubscriptionOptions {
  /** JWT token pro autentizaci s API. */
  token?: string;
  /** URL API (default window.location.origin). */
  apiUrl?: string;
}

interface UsePushSubscriptionResult {
  /** Prohlížeč podporuje Web Push. */
  isSupported: boolean;
  /** Aktuální stav povolení. */
  permission: NotificationPermission | "unknown";
  /** Je uživatel přihlášen k odběru notifikací. */
  isSubscribed: boolean;
  /** Probíhá operace. */
  isLoading: boolean;
  /** Chybová zpráva (null pokud vše OK). */
  error: string | null;
  /** Přihlásit se k odběru notifikací. Vrací `true` při úspěchu. */
  subscribe: () => Promise<boolean>;
  /** Odhlásit se z odběru notifikací. */
  unsubscribe: () => Promise<void>;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0));
}

async function getVapidPublicKey(apiUrl: string): Promise<string> {
  // POZOR: browser-facing API jsou pod /api/* (viz [[api-prefix-routing]]).
  // Bez /api spadne request na SPA fallback (index.html) → JSON parse selže.
  const res = await fetch(`${apiUrl}/api/push/vapid-public-key`);
  if (!res.ok) throw new Error("Nelze načíst VAPID klíč");
  const data = (await res.json()) as { publicKey: string };
  return data.publicKey;
}

/** Zaregistruje (upsert) subscription na serveru. Vrací true při úspěchu. */
async function registerOnServer(
  subscription: PushSubscription,
  apiUrl: string,
  token?: string,
): Promise<boolean> {
  const subJson = subscription.toJSON();
  const p256dh = subJson.keys?.p256dh;
  const auth = subJson.keys?.auth;
  if (!p256dh || !auth) throw new Error("Chybí push klíče.");

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${apiUrl}/api/push/subscribe`, {
    method: "POST",
    headers,
    body: JSON.stringify({ endpoint: subscription.endpoint, keys: { p256dh, auth } }),
  });
  if (!res.ok) throw new Error(`API chyba ${res.status}`);
  return true;
}

export function usePushSubscription({
  token,
  apiUrl = window.location.origin,
}: UsePushSubscriptionOptions = {}): UsePushSubscriptionResult {
  const isSupported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const [permission, setPermission] = useState<NotificationPermission | "unknown">(
    isSupported ? Notification.permission : "unknown",
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Při mountu zjisti stav subscription + auto-heal (viz JSDoc nahoře).
  useEffect(() => {
    if (!isSupported) return;
    void (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        let existing = await registration.pushManager.getSubscription();
        setPermission(Notification.permission);

        // Auto-heal — jen pokud je povolení uděleno (žádný prompt) a máme token,
        // přes který lze subscription přiřadit účtu na serveru.
        if (Notification.permission === "granted" && token) {
          if (!existing) {
            const vapidKey = await getVapidPublicKey(apiUrl);
            existing = await registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(vapidKey).buffer as ArrayBuffer,
            });
          }
          await registerOnServer(existing, apiUrl, token);
        }

        setIsSubscribed(existing !== null);
      } catch {
        // SW ještě není připraven / VAPID není nakonfigurován — tichý fallback.
      }
    })();
  }, [isSupported, token, apiUrl]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;
    setIsLoading(true);
    setError(null);

    try {
      // 1. Požádat o povolení
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setError("Notifikace byly zamítnuty.");
        return false;
      }

      // 2. Načíst VAPID public key
      const vapidKey = await getVapidPublicKey(apiUrl);

      // 3. Přihlásit se k Push API (vrátí existující subscription, pokud už je)
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey).buffer as ArrayBuffer,
      });

      // 4. Odeslat subscription na API (idempotentní upsert)
      await registerOnServer(subscription, apiUrl, token);
      setIsSubscribed(true);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Neznámá chyba");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, token, apiUrl]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;
    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        setIsSubscribed(false);
        return;
      }

      // Odhlásit z API
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      await fetch(`${apiUrl}/api/push/subscribe`, {
        method: "DELETE",
        headers,
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });

      // Odhlásit ze SW
      await subscription.unsubscribe();
      setIsSubscribed(false);
      setPermission(Notification.permission);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Neznámá chyba");
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, token, apiUrl]);

  return { isSupported, permission, isSubscribed, isLoading, error, subscribe, unsubscribe };
}
